import { useCallback, useEffect, useMemo, useState } from "react";
import { View, ViewProps } from "react-native";
import { useTranslation } from "react-i18next";
import { shallow } from "zustand/shallow";
import {
    DifficultyVeryEasy,
    DifficultyEasy,
    DifficultyMedium,
    DifficultyHard,
    difficultyFilterEvaluator,
    FilteredList,
    LengthFilterOption,
    lengthFilterEvaluator,
    DataProvider,
    PoiListContent,
    AoiListContent,
    statusFilterEvaluator,
    StatusFilterOption,
    useThemedStyleFunction,
    withExternalStyle,
    Path,
    Poi,
    Aoi,
    LocalizedToken,
    PathDifficulty,
    FeatureType,
    PathCategoryId,
    NavigationHeader,
} from "@sodraberget/shared";
import { useDataStore, useStateStore } from "../../../stores";
import { sideBarContentStyle } from "./side-bar-content.style";
import { getTranslatedProperty } from "../../../localization";
import { HierarchyPathProps, PathListHierarchyContent } from "./path-list-hierarchy-content.component";
import { isEqual } from "lodash";

type SideBarListProps = {
    navigation?: any;
} & ViewProps;

const SideBarList = (props: SideBarListProps) => {
    const { style: styleProp, navigation } = props;

    const [t] = useTranslation();
    const [expandedCompositePathIds, setExpandedCompositePathIds] = useState<Record<string, boolean>>({});
    const toggleCompositePath = useCallback((pathId: string) => {
        setExpandedCompositePathIds((current) => ({
            ...current,
            [pathId]: !(current[pathId] ?? false),
        }));
    }, []);
    
    // Get values and methods from stores
    const {
        paths,
        pathCategories,
        pois,
        poiCategories,
        aois,
        aoiCategories,
        areas,
        mainContent,
    } = useDataStore(state => state, shallow);
    const {
        selectedId,
        filters: currentFilters,
        setSelected,
        setFilters,
    } = useStateStore(state => state, shallow);

    // Helper: check if an object's areas match the area filter (by name, case-insensitive)
    const matchesAreas = (objectAreas: LocalizedToken[], filterAreas?: string[]) => {
        if (!filterAreas || filterAreas.length === 0) return true;
        return objectAreas.some(a => {
            const names = Object.values(a.name as any) as string[];
            return names.some(n => filterAreas.some(f => f.toLowerCase() === n.toLowerCase()));
        });
    };

    // Get filtered data
    const filteredPaths = useMemo(() => Object.values(paths ?? {})
        .filter((path: Path) => (mainContent.pathCategories?.length === 0 || mainContent.pathCategories?.includes(path.pathCategoryId))
            && matchesAreas(path.areas, mainContent.areas)), [paths, mainContent.pathCategories, mainContent.areas]);

    const hierarchicalPaths = useMemo(() => {
        const pathsById = new Map(filteredPaths.map((path) => [`${path.id}`, path]));
        const childIdSet = new Set<string>();

        filteredPaths.forEach((path) => {
            const childIds = Array.isArray(path?.compositeChildPathIds)
                ? path.compositeChildPathIds
                : [];
            childIds.forEach((childId: number | string) => childIdSet.add(`${childId}`));
        });

        const ordered: HierarchyPathProps[] = [];
        const pushed = new Set<string>();

        const addParent = (path: Path) => {
            const childIds = [...new Set((path.compositeChildPathIds ?? []).map(String))]
                .filter((id) => id !== `${path.id}` && pathsById.has(id));
            ordered.push({
                ...path,
                category: pathCategories?.[path.pathCategoryId],
                __hierarchyLevel: 0,
                __childCount: childIds.length,
                __expanded: expandedCompositePathIds[path.id] ?? false,
                __onToggle: childIds.length ? () => toggleCompositePath(path.id) : undefined,
            });
            return childIds;
        };

        filteredPaths.forEach((path) => {
            const pathId = `${path.id}`;
            if (childIdSet.has(pathId) && !path.compositeChildPathIds?.length) {
                return;
            }
            const childIds = addParent(path);
            pushed.add(pathId);

            childIds.forEach((childId: number | string) => {
                const child = pathsById.get(`${childId}`);
                if (child) {
                    ordered.push({
                        ...child,
                        category: pathCategories?.[child.pathCategoryId],
                        __hierarchyLevel: 1,
                        __parentPathId: pathId,
                    });
                    pushed.add(`${child.id}`);
                }
            });
        });

        filteredPaths.forEach((path) => {
            if (!pushed.has(`${path.id}`)) {
                addParent(path);
                pushed.add(`${path.id}`);
            }
        });

        return ordered;
    }, [filteredPaths, pathCategories, expandedCompositePathIds, toggleCompositePath]);
    const isPathEntryVisible = useCallback<NonNullable<DataProvider['isEntryVisible']>>((entry, entries) => {
        if (!('__parentPathId' in entry) || typeof entry.__parentPathId !== 'string') {
            return true;
        }
        const parentId = entry.__parentPathId;
        const parentIsListed = entries.some((candidate) => 'id' in candidate
            && `${candidate.id}` === parentId && !('__parentPathId' in candidate));
        return !parentIsListed || (expandedCompositePathIds[parentId] ?? false);
    }, [expandedCompositePathIds]);
    const filteredPois = useMemo(() => Object.values(pois ?? {})
        // Temperature POIs are map-only – they should never appear in the list. Match the
        // temperature category the same way as the map (category id 19) or its library icon.
        .filter((poi: Poi) => poi.poiCategoryId !== '19'
            && poiCategories?.[poi.poiCategoryId]?.iconKey !== 'thermometer'
            && (mainContent.poiCategories?.length === 0 || mainContent.poiCategories?.includes(poi.poiCategoryId))
            && matchesAreas(poi.areas, mainContent.areas)), [pois, poiCategories, mainContent.poiCategories, mainContent.areas]);
    const filteredAois = useMemo(() => Object.values(aois ?? {})
        .filter((aoi: Aoi) => (mainContent.aoiCategories?.length === 0 || mainContent.aoiCategories?.includes(aoi.aoiCategoryId))
            && matchesAreas(aoi.areas, mainContent.areas)), [aois, mainContent.aoiCategories, mainContent.areas]);

    // Check how many unique categories we're rendering
    const uniquePathCategories = [...new Set(filteredPaths.map((path: Path) => path.pathCategoryId))];
    const uniquePoiCategories = [...new Set(filteredPois.map((poi: Poi) => poi.poiCategoryId))];
    const uniqueAoiCategories = [...new Set(filteredAois.map((aoi: Aoi) => aoi.aoiCategoryId))];

    const availableAreas = useMemo(() => {
        const areaIds = new Set(
            [...filteredPaths, ...filteredPois, ...filteredAois]
                .flatMap((entry) => entry.areas.map((area) => `${area.id}`))
        );
        return Object.values(areas ?? {}).filter((area) => areaIds.has(`${area.id}`));
    }, [areas, filteredPaths, filteredPois, filteredAois]);

    const filters = useMemo(() => {
        const generatedFilters: any[] = [];

    // Add area filter if there's areas to chose from
    if (areas && Object.values(areas).length > 1) {
        const options = availableAreas
        .map((area: LocalizedToken) => ({
            name: getTranslatedProperty('name', area),
            value: `${area.id}`,
        }))
        .sort((a, b) => {
            const al = a.name.toLowerCase();
            const bl = b.name.toLowerCase();

            return al < bl ? -1 : al > bl ? 1 : 0;
        });

        const evaluator = (entry: any, option: any) => {
            return !option || entry.areas.map((area: LocalizedToken) => `${area.id}`).includes(option);
        };

        generatedFilters.push({identifier: 'area', name: t('filters:area'), unsetName: t('filters:areaAll'), options, evaluator});
    }

    // Add length filter if objets with length are present
    if (uniquePathCategories.length > 0) {
        generatedFilters.push({
            identifier: 'length',
            name: t('filters:length'),
            unsetName: t('filters:lengthAll'),
            options: [
                {name: '0-3 km', value: LengthFilterOption.ZeroToThree},
                {name: '3-6 km', value: LengthFilterOption.ThreeToSix},
                {name: '6-10 km', value: LengthFilterOption.SixToTen},
                {name: '10+ km', value: LengthFilterOption.TenPlus},
            ],
            evaluator: lengthFilterEvaluator,
        });
    }

    // Add difficulty filter if objects with difficulty are present
    if (uniquePathCategories.length > 0) {
        generatedFilters.push({
            identifier: 'difficulty',
            name: t('filters:difficulty'),
            unsetName: t('filters:difficultyAll'),
            options: [
                {name: t('veryEasyPlural'), value: PathDifficulty.VeryEasy, icon: DifficultyVeryEasy},
                {name: t('easyPlural'), value: PathDifficulty.Easy, icon: DifficultyEasy},
                {name: t('mediumPlural'), value: PathDifficulty.Medium, icon: DifficultyMedium},
                {name: t('hardPlural'), value: PathDifficulty.Hard, icon: DifficultyHard},
            ],
            evaluator: difficultyFilterEvaluator,
        });
    }

    // Add status filter if objects with status are present
    if (uniquePathCategories.length > 0) {
        generatedFilters.push({
            identifier: 'status',
            name: t('filters:status'),
            unsetName: t('filters:statusAll'),
            options: [
                {name: t('filters:statusOpen'), value: StatusFilterOption.Open },
                ...( uniquePathCategories.includes(PathCategoryId.CrossCountrySkiing) ? [
                    {name: t('filters:statusGroomedToday'), value: StatusFilterOption.GroomedToday},
                ] : []),
                {name: t('filters:statusClosed'), value: StatusFilterOption.Closed },
                ...( uniquePathCategories.includes(PathCategoryId.CrossCountrySkiing) ? [
                    {name: t('filters:statusGroomedYesterdayOrToday'), value: StatusFilterOption.GroomedLatestYesterday},
                ] : []),
            ],
            evaluator: statusFilterEvaluator,
        });
    }

        return generatedFilters;
    }, [
        areas,
        availableAreas,
        t,
        uniquePathCategories,
    ]);

    const dataProviders = useMemo(() => ([
        ...(hierarchicalPaths?.length ? [{
            entries: hierarchicalPaths,
            component: PathListHierarchyContent,
            isEntryVisible: isPathEntryVisible,
        }] : []),
        ...(filteredPois?.length ? [{ entries: filteredPois.map((poi: Poi) => ({
                ...poi,
                ...(poiCategories ? {category: poiCategories[poi.poiCategoryId]} : {})
            })),
            component: PoiListContent,
        }] : []),
        ...(filteredAois?.length ? [{ entries: filteredAois.map((aoi: Aoi) => ({
                ...aoi,
                ...(aoiCategories ? {category: aoiCategories[aoi.aoiCategoryId]} : {})
            })),
            component: AoiListContent,
        }] : []),
    ]), [hierarchicalPaths, filteredPois, filteredAois, poiCategories, aoiCategories, isPathEntryVisible]);

    // Do navigation based on changes to selection
    useEffect(() => {
        if (selectedId !== undefined) {
            navigation?.navigate("details");
        }
        else {
            navigation?.navigate("list");
        }
    }, [navigation, selectedId]);

    const handleListClick = (providerIndex: number, entryIndex: number) => {
        const entry = dataProviders[providerIndex]?.entries[entryIndex];

        if (!entry || typeof entry !== 'object') {
            return;
        }

        const hasProp = (prop: string) => Object.prototype.hasOwnProperty.call(entry, prop);

        // Entry is a POI
        if (hasProp('poiCategoryId')) {
            console.log('POI', entry);
            setSelected(FeatureType.Poi, (entry as { id: string }).id, 'list');
        }
        // Entry is a Path
        else if (hasProp('pathCategoryId')) {
            console.log('Path', entry);
            setSelected(FeatureType.Path, (entry as { id: string }).id, 'list');
        }
        // Entry is a Aoi
        else if (hasProp('aoiCategoryId')) {
            console.log('AOI', entry);
            setSelected(FeatureType.Aoi, (entry as { id: string }).id, 'list');
        }
    };

    const handleOnFiltered = (filteredDataProviders: any[]) => {
        const originalEntries = dataProviders.map((provider) => provider.entries).flat();
        const entries = filteredDataProviders.map((provider) => provider.entries).flat();

        // Check if list is unfiltered, set filters accordingly
        if (originalEntries.length == entries.length) {
            setFilters([]);
            return;
        }

        const pathIds: string[] = [];
        const poiIds: string[] = [];
        const aoiIds: string[] = [];
        entries.forEach((entry: any) => {
            // Path
            if (entry.pathCategoryId !== undefined) {
                pathIds.push(entry.id);
            }
            // Poi
            else if (entry.poiCategoryId !== undefined) {
                poiIds.push(entry.id);
            }
            //Aoi
            else if (entry.aoiCategoryId !== undefined) {
                aoiIds.push(entry.id);
            }
        });

        const nextFilters = [
            ...(pathIds.length > 0 ? [{type: FeatureType.Path, ids: [...new Set(pathIds)]}] : []),
            ...(poiIds.length > 0 ? [{type: FeatureType.Poi, ids: poiIds}] : []),
            ...(aoiIds.length > 0 ? [{type: FeatureType.Aoi, ids: aoiIds}] : []),
        ];

        if (!isEqual(currentFilters ?? [], nextFilters)) {
            setFilters(nextFilters);
        }
    };

    const style = useThemedStyleFunction(sideBarContentStyle);
    const containerStyle = withExternalStyle(style.container, styleProp);

    let title = process.env.REACT_APP_CUSTOMER_NAME || t('destinationNamePlaceholder');
    // Get title if a unique path category is listed
    if (pathCategories && uniquePathCategories.length === 1 && uniquePoiCategories.length === 0) {
        title = getTranslatedProperty('name', pathCategories[uniquePathCategories[0]]);
    }
    // Get title if no paths are listed but a unique PoI category is listed
    else if (poiCategories && uniquePathCategories.length === 0 && uniquePoiCategories.length === 1) {
        title = getTranslatedProperty('name', poiCategories[uniquePoiCategories[0]]);
    }

    // Get title if no paths/pois are listed but a unique AoI category is listed
    else if (aoiCategories && uniquePathCategories.length === 0 && uniquePoiCategories.length === 0 
        && uniqueAoiCategories.length === 1 ) {
        title = getTranslatedProperty('name', aoiCategories[uniqueAoiCategories[0]]);
    }

    return (
        <View style={containerStyle}>
            <NavigationHeader title={title} leftAlign={true} />
            { areas && 
                <FilteredList style={style.list}
                    emptyText={t('filters:emptyResult')}
                    dataProviders={dataProviders}
                    filters={filters}
                    onPress={(pI: number, eI: number) => handleListClick(pI, eI)}
                    onFiltered={(dataProviders: any[]) => handleOnFiltered(dataProviders)}/>
            }
        </View>
    );
};

export { SideBarList };